/**
 * O kit visual do cadastro e do onboarding novos (A062, fase C), a partir do
 * protótipo da Mari (Figma "Eva maternidade", seções Cadastro e Onboarding).
 *
 * Cores vêm do tema da marca, nunca fixas: o mesmo cadastro serve à Eva
 * Maternidade e às marcas corporativas, cada uma com a sua identidade. O azul
 * do protótipo é o primário da Eva Maternidade (`--eva-brand`); o degradê do
 * cadastro clareia essa primária (`degradeDoCadastro`). O rosa do protótipo
 * — ações secundárias, a opção escolhida, a foto — é a cor de destaque da
 * marca (A095): `--eva-brand-accent` em borda, anel e selo, e
 * `--eva-brand-accent-text` (a mesma, escurecida até ler sobre branco) em
 * texto. Sem destaque escolhido, é a primária. Até a A095 esse papel era do
 * tom 7 da primária.
 *
 * Fontes: Poppins nos títulos, Instrument Sans no texto (carregadas no
 * `index.html`), sempre com a fonte do app como queda.
 */
import { Loader } from '@mantine/core';
import { IconChevronLeft, IconLock } from '@tabler/icons-react';
import { useEffect, useId, useLayoutEffect, useRef, useState, type CSSProperties, type KeyboardEvent, type ReactNode } from 'react';
import { useBrand } from '@eva/domain';
import { BrandLogo, corDaBoneca, degradeDoCadastro, degradeDoOnboarding, tokensFromBrand, tomClaroDaMarca } from '@eva/ui';

export const FONTE_TITULO = "'Poppins', var(--eva-font)";
export const FONTE_TEXTO = "'Instrument Sans', var(--eva-font)";

/**
 * O logo no cadastro e no onboarding: bem maior que nos portais (06/10/2026,
 * era 44 px). A largura máxima ainda deixa o botão de voltar livre ao lado.
 */
export const LOGO = { height: 80, maxWidth: 220 } as const;

/** Fundo das telas do onboarding: branco no topo, tom 2 da marca embaixo (medido no protótipo). */
/** A primária desta marca, de onde saem os degradês e o tom claro (A087). */
function usePrimariaDaMarca(): string {
  const { brand } = useBrand();
  return tokensFromBrand(brand.branding?.colors ?? null).primary;
}

/** O degradê do onboarding desta marca: branco até o tom claro (A087). */
export function useDegradeDoOnboarding(): string {
  return degradeDoOnboarding(usePrimariaDaMarca());
}

/** A cor da roupa da personagem da abertura, pela primária desta marca (A091). */
export function useCorDaBoneca() {
  return corDaBoneca(usePrimariaDaMarca());
}

/**
 * Onde moram as versões do aceno da abertura, uma pasta por cor (A091): cada
 * uma com `aceno.mp4` e `fim.jpg` (o último quadro, para "reduzir movimento").
 */
export const PASTA_DA_SAUDACAO = '/illustrations/boas-vindas';

/**
 * As figuras da escolha de momento ("Minha jornada"), em
 * `PASTA_DOS_MOMENTOS/<figura>.jpg`. A lista mora aqui para a pré-carga
 * saber quais baixar; `MOMENTOS`, no onboarding, só aceita estes nomes.
 */
export const PASTA_DOS_MOMENTOS = '/illustrations/momentos';
export const FIGURAS_DOS_MOMENTOS = ['tentante', 'gestante', 'pos-parto', 'retorno-ao-trabalho', 'mae-de-anjo', 'parceiro'] as const;
export type FiguraDoMomento = (typeof FIGURAS_DOS_MOMENTOS)[number];

/**
 * O fundo de cada figura: a cor da roupa, bem clara, para dar contraste
 * (06/10/2026). Creme, verde e rosa são os valores do protótipo; os demais
 * seguem o mesmo padrão (claridade ~96%, saturação ~85%) no matiz da roupa.
 * Vale para o cartão da escolha de momento e para o círculo da figura em
 * Perfil → Fase da Maternidade.
 */
export const FUNDO_DA_FIGURA: Record<FiguraDoMomento, string> = {
  tentante: '#FDEAE7',
  gestante: '#ECF3FD',
  'pos-parto': '#EAF9F4',
  'retorno-ao-trabalho': '#F3ECFD',
  'mae-de-anjo': '#FEF9EC',
  parceiro: '#FDF2E7',
};

const jaPedidos = new Set<string>();

/**
 * Baixa em segundo plano o que o começo do onboarding mostra, para ele não
 * esperar a rede: o aceno desta marca (A092) e as seis figuras da escolha de
 * momento (A109 — chegavam aos poucos, uma a uma, enquanto o carrossel já
 * estava na tela). Chamado em "Cadastro concluído", a tela anterior, e na
 * entrada do onboarding. Só aquece o cache do navegador; quem mostra é a
 * própria tela. Com "reduzir movimento", do aceno só a imagem parada.
 */
export function usePreCarregarOnboarding() {
  const cor = useCorDaBoneca();
  useEffect(() => {
    const parado = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    const pasta = `${PASTA_DA_SAUDACAO}/${cor}`;
    const urls = [
      ...(parado ? ['fim.jpg'] : ['aceno.mp4', 'fim.jpg']).map((arquivo) => `${pasta}/${arquivo}`),
      ...FIGURAS_DOS_MOMENTOS.map((figura) => `${PASTA_DOS_MOMENTOS}/${figura}.jpg`),
    ];
    for (const url of urls) {
      if (jaPedidos.has(url)) continue;
      jaPedidos.add(url);
      fetch(url, { priority: 'low' } as RequestInit).catch(() => jaPedidos.delete(url));
    }
  }, [cor]);
}

/** O tom claro desta marca, calculado só da primária (A087). */
export function useTomClaroDaMarca(): string {
  return tomClaroDaMarca(usePrimariaDaMarca());
}

const COR = {
  primaria: 'var(--eva-brand)',
  /**
   * Texto sobre a cor primária: sempre branco, como no protótipo (06/10/2026).
   * O `--eva-brand-contrast` escolhe quase-preto para primária clara, e o
   * "Continuar" saía escuro sobre o azul-claro da Eva Maternidade.
   */
  contraste: '#FFFFFF',
  /** O destaque em texto: escurecido até 4,5:1 sobre branco (A095). */
  acento: 'var(--eva-brand-accent-text)',
  /** O destaque em borda, anel e selo (A095). */
  destaque: 'var(--eva-brand-accent)',
  texto: '#000000',
  textoFraco: '#808080',
  borda: '#F1F1F1',
  bordaForte: '#BEBEBE',
  fundo: '#F9F9F9',
  branco: '#FFFFFF',
};

const coluna: CSSProperties = {
  position: 'relative',
  width: '100%',
  maxWidth: 'var(--eva-phone-column)',
  minHeight: '100dvh',
  display: 'flex',
  flexDirection: 'column',
  fontFamily: FONTE_TEXTO,
  color: COR.texto,
};

function BotaoVoltar({ onClick, fundo = COR.branco }: { onClick: () => void; fundo?: string }) {
  return (
    <button
      type="button"
      aria-label="Voltar"
      onClick={onClick}
      style={{ width: 44, height: 44, borderRadius: 15, border: 'none', background: fundo, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flex: 'none' }}
    >
      <IconChevronLeft size={24} stroke={1.5} color={COR.texto} />
    </button>
  );
}

// --- Cadastro ----------------------------------------------------------------


/**
 * Moldura das telas do cadastro, medida sobre o protótipo (06/10/2026, tela
 * de 393×852): o degradê da marca no topo (`degradeDoCadastro`) até y=400,
 * com os cantos de baixo arredondados (40) e o fundo cinza-claro depois; o voltar (44 px, em 20/65) e o logo centrado na
 * mesma altura dele; e o cartão branco a partir de y=149, 20 px das bordas,
 * esticado até perto do fim da tela. Antes, o degradê partia de um tom claro e
 * sumia no branco antes do cartão, e o logo maior descia abaixo do voltar.
 *
 * `cheia` pinta o mesmo degradê na tela toda e centraliza o cartão (código
 * e conclusão) — como no protótipo; antes, ali, o degradê começava claro em
 * cima.
 *
 * O logo é o de fundo colorido, quando a marca tem um (A076). O contorno
 * branco de 1 px que o logo levava aqui saiu de vez (07/10/2026, A078): era
 * um remendo para o logo de fundo claro sobre a cor, e a prévia do editor
 * de identidade agora avisa quando o logo some e oferece a versão branca.
 */
/** O degradê do topo do cadastro mora em `@eva/ui` (A075), com a prévia da identidade. */
export { degradeDoCadastro };

export function MolduraDoCadastro({ onBack, cheia = false, children, rodape }: { onBack?: () => void; cheia?: boolean; children: ReactNode; rodape?: ReactNode }) {
  const { brand } = useBrand();
  const degradeDoTopo = degradeDoCadastro(tokensFromBrand(brand.branding?.colors ?? null).primary);
  const degradeCheio = degradeDoTopo;
  return (
    <div style={{ minHeight: '100dvh', display: 'flex', justifyContent: 'center', background: COR.fundo }}>
      <div style={{ ...coluna, background: cheia ? degradeCheio : COR.fundo }}>
        {!cheia && <div aria-hidden style={{ position: 'absolute', inset: '0 0 auto 0', height: 400, background: degradeDoTopo, borderRadius: '0 0 40px 40px' }} />}
        {/* Linha do voltar: 44 px a partir de 65; o logo fica centrado nela, mesmo maior que ela. */}
        <div style={{ position: 'relative', height: 109, flex: 'none' }}>
          {onBack && (
            <div style={{ position: 'absolute', left: 20, top: 65, viewTransitionName: 'eva-voltar' }}>
              <BotaoVoltar onClick={onBack} />
            </div>
          )}
          <div style={{ position: 'absolute', left: '50%', top: 87, transform: 'translate(-50%, -50%)', viewTransitionName: 'eva-logo' }}>
            <BrandLogo name={brand.name} logoUrl={brand.branding?.logoUrl} logoSobreCorUrl={(brand.branding as any)?.logoSobreCorUrl} fundo="cor" logoHasOwnBackground={brand.branding?.logoHasOwnBackground} {...LOGO} />
          </div>
        </div>
        <div style={{ position: 'relative', flex: 1, display: 'flex', flexDirection: 'column', justifyContent: cheia ? 'center' : 'flex-start', padding: cheia ? '0 20px 80px' : '40px 20px 32px' }}>
          {/* O cartão fica e só ajusta a altura; o conteúdo dele é que desliza (`cadastro/transicao.ts`). */}
          <div style={{ background: COR.branco, borderRadius: 30, padding: '24px 16px', boxShadow: cheia ? '0 4px 37px rgba(0,0,0,0.03)' : undefined, flex: cheia ? undefined : 1, viewTransitionName: 'eva-cartao' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20, viewTransitionName: 'eva-conteudo' }}>{children}</div>
          </div>
        </div>
        {rodape && <div style={{ position: 'relative', padding: '0 20px 32px', display: 'flex', justifyContent: 'center', gap: 20 }}>{rodape}</div>}
      </div>
    </div>
  );
}

export function TituloDoCartao({ titulo, subtitulo, centralizado = false }: { titulo: ReactNode; subtitulo?: ReactNode; centralizado?: boolean }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, alignItems: centralizado ? 'center' : 'flex-start', textAlign: centralizado ? 'center' : 'left' }}>
      <h1 style={{ margin: 0, fontFamily: FONTE_TITULO, fontWeight: 700, fontSize: 30, lineHeight: '120%', textWrap: 'balance' as any }}>{titulo}</h1>
      {subtitulo && <p style={{ margin: 0, fontSize: 14, lineHeight: '140%', color: COR.textoFraco }}>{subtitulo}</p>}
    </div>
  );
}

export function CampoDeTexto({
  id,
  rotulo,
  icone,
  valor,
  onChange,
  tipo = 'text',
  placeholder,
  erro,
  apoio,
  direita,
  autoComplete,
  inputMode,
  onFocus,
  onBlur,
}: {
  id: string;
  rotulo: string;
  icone?: ReactNode;
  valor: string;
  onChange: (v: string) => void;
  tipo?: string;
  placeholder?: string;
  erro?: string;
  apoio?: string;
  direita?: ReactNode;
  autoComplete?: string;
  inputMode?: 'text' | 'email' | 'tel' | 'numeric';
  onFocus?: () => void;
  onBlur?: () => void;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, width: '100%' }}>
      <label htmlFor={id} style={{ fontSize: 16, fontWeight: 500 }}>{rotulo}</label>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, height: 50, padding: '0 15px', borderRadius: 40, border: `1px solid ${erro ? 'var(--mantine-color-red-6)' : COR.borda}`, background: COR.branco }}>
        {icone && <span style={{ display: 'flex', color: valor ? COR.texto : COR.textoFraco }}>{icone}</span>}
        <input
          id={id}
          type={tipo}
          value={valor}
          placeholder={placeholder}
          autoComplete={autoComplete}
          inputMode={inputMode}
          onChange={(e) => onChange(e.currentTarget.value)}
          onFocus={onFocus}
          onBlur={onBlur}
          aria-invalid={erro ? true : undefined}
          aria-describedby={erro || apoio ? `${id}-ajuda` : undefined}
          style={{ flex: 1, minWidth: 0, border: 'none', outline: 'none', background: 'transparent', fontFamily: FONTE_TEXTO, fontSize: 14, color: COR.texto }}
        />
        {direita}
      </div>
      {(erro || apoio) && (
        <span id={`${id}-ajuda`} style={{ fontSize: 12, color: erro ? 'var(--mantine-color-red-6)' : COR.textoFraco, paddingLeft: 15 }}>{erro ?? apoio}</span>
      )}
    </div>
  );
}

export function BotaoPrincipal({ children, onClick, disabled, loading, tipo = 'button', noDestaque, travado }: { children: ReactNode; onClick?: () => void; disabled?: boolean; loading?: boolean; tipo?: 'button' | 'submit'; noDestaque?: boolean; travado?: boolean }) {
  const inativo = disabled || loading;
  return (
    <button
      type={tipo}
      onClick={onClick}
      disabled={inativo}
      style={{ width: '100%', height: 52, borderRadius: 40, border: 'none', background: travado ? 'var(--eva-brand-tom-claro)' : noDestaque ? COR.destaque : COR.primaria, color: travado ? COR.acento : COR.contraste, fontFamily: FONTE_TITULO, fontWeight: 600, fontSize: 16, cursor: inativo ? 'default' : 'pointer', opacity: disabled && !loading ? 0.5 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
    >
      {loading ? <Loader size={20} color="white" /> : (
        <>
          {travado && <IconLock size={18} stroke={2} aria-hidden />}
          {children}
        </>
      )}
    </button>
  );
}

export function BotaoContorno({ children, onClick }: { children: ReactNode; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{ width: '100%', height: 51, borderRadius: 40, border: `1px solid ${COR.bordaForte}`, background: 'transparent', color: COR.texto, fontFamily: FONTE_TEXTO, fontWeight: 600, fontSize: 16, cursor: 'pointer' }}
    >
      {children}
    </button>
  );
}

/** Ação secundária em texto: "Fazer login", "Pular", "Prefiro não registrar". */
export function LinkDeAcao({ children, onClick, peso = 600 }: { children: ReactNode; onClick: () => void; peso?: number }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{ background: 'none', border: 'none', padding: 4, color: COR.acento, fontFamily: FONTE_TITULO, fontWeight: peso, fontSize: 16, cursor: 'pointer' }}
    >
      {children}
    </button>
  );
}

// --- Onboarding --------------------------------------------------------------

export interface AcaoDoOnboarding {
  texto: string;
  onClick: () => void;
  disabled?: boolean;
  loading?: boolean;
  /**
   * O botão na cor de destaque, e não na primária (14/10/2026): na escolha
   * da jornada ele acompanha a borda do cartão escolhido. Na Eva Maternidade
   * a primária é um azul-claro, e o "Continuar" parecia desabilitado ali.
   */
  noDestaque?: boolean;
  /**
   * A escolha está travada por um bloqueio da marca (14/10/2026): o botão vai
   * no tom claro, com cadeado, e o toque mostra o aviso em vez de avançar.
   */
  travado?: boolean;
}

/**
 * Moldura de um passo do onboarding: voltar, a barra de progresso com
 * "n / total", título e subtítulo, o conteúdo no meio, e embaixo a ação
 * secundária (pular) e o "Continuar".
 */
export function MolduraDoOnboarding({
  passo,
  total,
  onBack,
  titulo,
  subtitulo,
  children,
  continuar,
  secundaria,
}: {
  passo: number;
  total: number;
  onBack?: () => void;
  titulo: ReactNode;
  subtitulo?: ReactNode;
  children: ReactNode;
  continuar: AcaoDoOnboarding;
  secundaria?: { texto: string; onClick: () => void };
}) {
  const degrade = useDegradeDoOnboarding();
  const tomClaro = useTomClaroDaMarca();
  return (
    <div style={{ minHeight: '100dvh', display: 'flex', justifyContent: 'center', background: 'var(--eva-brand-0)' }}>
      {/* Branco no topo até o tom claro da marca embaixo, como no protótipo (A087). */}
      <div style={{ ...coluna, background: degrade, padding: '65px 20px 32px', gap: 20 }}>
        {/* Voltar, passos e contador, a 26 px um do outro, como no protótipo (A088). */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 26 }}>
          {onBack ? (
            <div style={{ viewTransitionName: 'eva-voltar' }}>
              <BotaoVoltar onClick={onBack} fundo={COR.fundo} />
            </div>
          ) : (
            <span style={{ width: 44 }} />
          )}
          {/* A barra fica; o passo ativo desliza até o novo (`cadastro/transicao.ts`). */}
          <div role="progressbar" aria-valuemin={1} aria-valuemax={total} aria-valuenow={passo} aria-label={`Passo ${passo} de ${total}`} style={{ flex: 1, display: 'flex', gap: 3, viewTransitionName: 'eva-progresso' }}>
            {Array.from({ length: total }, (_, i) => (
              // Os que faltam no tom claro da marca (A088): o tom 1 de antes,
              // quase branco, sumia no topo branco do degradê.
              <span key={i} style={{ flex: 1, height: 8, borderRadius: 40, background: i === passo - 1 ? COR.primaria : tomClaro, viewTransitionName: i === passo - 1 ? 'eva-passo-ativo' : undefined }} />
            ))}
          </div>
          <span style={{ fontFamily: FONTE_TITULO, fontWeight: 600, fontSize: 16, minWidth: 34, textAlign: 'right', fontVariantNumeric: 'tabular-nums', viewTransitionName: 'eva-contador' }}>{passo} / {total}</span>
        </div>

        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 20, minHeight: 0, viewTransitionName: 'eva-conteudo' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, textAlign: 'center' }}>
            <h1 style={{ margin: 0, fontFamily: FONTE_TITULO, fontWeight: 700, fontSize: 30, lineHeight: '120%', textWrap: 'balance' as any }}>{titulo}</h1>
            {subtitulo && <p style={{ margin: 0, fontFamily: FONTE_TITULO, fontSize: 16, lineHeight: '140%', color: COR.textoFraco }}>{subtitulo}</p>}
          </div>

          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 16, minHeight: 0 }}>{children}</div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, viewTransitionName: 'eva-acoes' }}>
          {secundaria && <LinkDeAcao peso={500} onClick={secundaria.onClick}>{secundaria.texto}</LinkDeAcao>}
          <BotaoPrincipal onClick={continuar.onClick} disabled={continuar.disabled} loading={continuar.loading} noDestaque={continuar.noDestaque} travado={continuar.travado}>{continuar.texto}</BotaoPrincipal>
        </div>
      </div>
    </div>
  );
}

/**
 * Lista de opções em cartões; a escolhida ganha a borda do destaque (A095).
 * Para poucas opções (duas, três), que cabem inteiras na tela. Medidas do
 * protótipo (A112): cartão de 78 px, 20 px entre eles, texto de 18 px.
 */
export function ListaDeEscolhas<T extends string>({ opcoes, valor, onChange, rotulo }: { opcoes: Array<{ valor: T; texto: string; apoio?: string }>; valor: T | null | undefined; onChange: (v: T) => void; rotulo: string }) {
  return (
    <div role="radiogroup" aria-label={rotulo} style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {opcoes.map((o) => {
        const ativa = o.valor === valor;
        return (
          <button
            key={o.valor}
            type="button"
            role="radio"
            aria-checked={ativa}
            aria-label={o.apoio ? `${o.texto}: ${o.apoio}` : o.texto}
            onClick={() => onChange(o.valor)}
            style={{ minHeight: ALTURA_DO_CARTAO, padding: '14px 12px', borderRadius: 15, background: COR.branco, border: `2px solid ${ativa ? COR.destaque : 'transparent'}`, fontFamily: FONTE_TITULO, fontWeight: 700, fontSize: 18, lineHeight: '120%', color: COR.texto, cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2 }}
          >
            {o.texto}
            {o.apoio && <span style={{ fontWeight: 400, fontSize: 13, color: COR.textoFraco }}>{o.apoio}</span>}
          </button>
        );
      })}
    </div>
  );
}

/** O cartão de opção do protótipo (A112): 78 px de altura, 20 px entre um e outro. */
const ALTURA_DO_CARTAO = 78;
const ENTRE_CARTOES = 20;
const PASSO_DA_RODA = ALTURA_DO_CARTAO + ENTRE_CARTOES;

/** Opacidade pelo afastamento do centro, em cartões: 1 no centro, 0,45 ao lado, 0,1 nas pontas. */
function opacidadeNaRoda(distancia: number): number {
  if (distancia <= 1) return 1 - 0.55 * distancia;
  if (distancia <= 2) return 0.45 - 0.35 * (distancia - 1);
  return Math.max(0, 0.1 - 0.1 * (distancia - 2));
}

/**
 * A roda de cartões do protótipo (A112), para cinco opções: rola na
 * vertical e encaixa no cartão do meio, que é o escolhido — com a borda do
 * destaque. Os vizinhos esmaecem (0,45), e os das pontas quase somem (0,1).
 *
 * Na altura do protótipo (470 px) mostra cinco cartões; em tela baixa
 * encolhe até três, e o centro continua no meio — a altura é medida.
 *
 * Como a `Roleta`: arrastar, a roda do mouse ou as setas mudam; tocar num
 * cartão o traz ao centro; a escolha sobe quando a rolagem para. Sem valor,
 * abre no cartão do meio, com o texto em cinza e sem a borda — é só uma
 * sugestão, e o "Continuar" espera a pessoa escolher (mexer ou tocar).
 */
export function RodaDeEscolhas<T extends string>({ opcoes, valor, onChange, rotulo }: { opcoes: Array<{ valor: T; texto: string }>; valor: T | null | undefined; onChange: (v: T) => void; rotulo: string }) {
  const caixa = useRef<HTMLDivElement>(null);
  const cartoes = useRef<Array<HTMLDivElement | null>>([]);
  const parada = useRef(0);
  // Só a pessoa escolhe: a rolagem que a própria roda faz ao abrir (para pôr
  // o cartão no centro) dispara o mesmo evento, e escolhia sozinha a sugestão.
  const mexeu = useRef(false);
  const id = useId();
  const confirmado = valor != null && opcoes.some((o) => o.valor === valor);
  const indice = confirmado ? opcoes.findIndex((o) => o.valor === valor) : Math.floor((opcoes.length - 1) / 2);
  const [centro, setCentro] = useState(indice);
  // Espaço antes do primeiro e depois do último, para eles também chegarem ao centro.
  const [margem, setMargem] = useState(0);

  const esmaecer = () => {
    const el = caixa.current;
    if (!el) return;
    cartoes.current.forEach((c, i) => {
      if (c) c.style.opacity = String(opacidadeNaRoda(Math.abs(i * PASSO_DA_RODA - el.scrollTop) / PASSO_DA_RODA));
    });
  };

  // A altura é a que coube (até 470 px): o espaço das pontas sai dela.
  useLayoutEffect(() => {
    const el = caixa.current;
    if (!el) return;
    const medir = () => setMargem(Math.max(0, (el.clientHeight - PASSO_DA_RODA) / 2));
    medir();
    const observador = new ResizeObserver(medir);
    observador.observe(el);
    return () => observador.disconnect();
  }, []);

  // Segue o valor de fora (e a margem, ao medir): o cartão escolhido no centro.
  useLayoutEffect(() => {
    const el = caixa.current;
    if (!el) return;
    if (Math.abs(el.scrollTop - indice * PASSO_DA_RODA) > 1) el.scrollTop = indice * PASSO_DA_RODA;
    setCentro(indice);
    esmaecer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [indice, margem]);

  const aoRolar = () => {
    const el = caixa.current;
    if (!el) return;
    esmaecer();
    const i = Math.min(opcoes.length - 1, Math.max(0, Math.round(el.scrollTop / PASSO_DA_RODA)));
    setCentro(i);
    if (!mexeu.current) return;
    window.clearTimeout(parada.current);
    parada.current = window.setTimeout(() => {
      if (opcoes[i].valor !== valor) onChange(opcoes[i].valor);
    }, 120);
  };

  const irPara = (i: number) => {
    mexeu.current = true;
    const alvo = Math.min(opcoes.length - 1, Math.max(0, i));
    // Já no centro, não há rolagem para confirmar: confirma direto.
    if (alvo === centro) {
      if (opcoes[alvo].valor !== valor) onChange(opcoes[alvo].valor);
      return;
    }
    const reduzido = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    caixa.current?.scrollTo({ top: alvo * PASSO_DA_RODA, behavior: reduzido ? 'auto' : 'smooth' });
  };

  const teclas = (e: KeyboardEvent) => {
    const passos: Record<string, number> = { ArrowUp: -1, ArrowDown: 1 };
    if (e.key in passos) irPara(centro + passos[e.key]);
    else if (e.key === 'Home') irPara(0);
    else if (e.key === 'End') irPara(opcoes.length - 1);
    else if (e.key === 'Enter' || e.key === ' ') irPara(centro);
    else return;
    e.preventDefault();
  };

  return (
    <div
      ref={caixa}
      className="eva-roleta"
      role="listbox"
      aria-label={rotulo}
      aria-activedescendant={`${id}-${centro}`}
      tabIndex={0}
      onScroll={aoRolar}
      onKeyDown={teclas}
      onPointerDown={() => (mexeu.current = true)}
      onWheel={() => (mexeu.current = true)}
      onTouchStart={() => (mexeu.current = true)}
      style={{
        // 470 px é o protótipo (cinco cartões); a conta desconta o resto da tela, e o mínimo mostra três.
        height: `min(${5 * ALTURA_DO_CARTAO + 4 * ENTRE_CARTOES}px, max(${3 * ALTURA_DO_CARTAO + 2 * ENTRE_CARTOES}px, calc(100dvh - 350px)))`,
        overflowY: 'auto',
        scrollSnapType: 'y mandatory',
        scrollbarWidth: 'none',
        overscrollBehavior: 'contain',
        outline: 'none',
      }}
    >
      <style>{'.eva-roleta::-webkit-scrollbar{display:none}'}</style>
      <div style={{ height: margem }} />
      {opcoes.map((o, i) => {
        const noCentro = i === centro;
        const escolhido = noCentro && confirmado && o.valor === valor;
        return (
          <div key={o.valor} style={{ height: PASSO_DA_RODA, display: 'flex', alignItems: 'center', scrollSnapAlign: 'center' }}>
            <div
              ref={(el) => {
                cartoes.current[i] = el;
              }}
              id={`${id}-${i}`}
              role="option"
              aria-selected={escolhido}
              onClick={() => irPara(i)}
              style={{
                width: '100%',
                height: ALTURA_DO_CARTAO,
                boxSizing: 'border-box',
                padding: '14px 12px',
                borderRadius: 15,
                background: COR.branco,
                border: `2px solid ${escolhido ? COR.destaque : 'transparent'}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                fontFamily: FONTE_TITULO,
                fontWeight: 700,
                fontSize: 18,
                lineHeight: '120%',
                color: noCentro && !confirmado ? COR.textoFraco : COR.texto,
                cursor: 'pointer',
                userSelect: 'none',
                transition: 'border-color 120ms ease, color 120ms ease',
              }}
            >
              {o.texto}
            </div>
          </div>
        );
      })}
      <div style={{ height: margem }} />
    </div>
  );
}

/** Um campo só, grande, no meio da tela: apelido, nome do bebê. */
export function CampoGrande({ id, valor, onChange, placeholder, rotulo, maxLength = 60 }: { id: string; valor: string; onChange: (v: string) => void; placeholder: string; rotulo: string; maxLength?: number }) {
  return (
    <input
      id={id}
      aria-label={rotulo}
      value={valor}
      maxLength={maxLength}
      placeholder={placeholder}
      onChange={(e) => onChange(e.currentTarget.value)}
      style={{ width: '100%', height: 78, borderRadius: 15, border: 'none', background: COR.branco, textAlign: 'center', fontFamily: FONTE_TITULO, fontWeight: 700, fontSize: 18, color: COR.texto, outline: 'none' }}
    />
  );
}

const MESES = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

const ALTURA_DO_ITEM = 40;
const ITENS_VISIVEIS = 5;
const ESPACO = ((ITENS_VISIVEIS - 1) / 2) * ALTURA_DO_ITEM;

/**
 * Uma coluna de roleta, como no protótipo (06/10/2026): rola na vertical e
 * encaixa no item do meio, que é o escolhido. Antes, eram `<select>` nativos:
 * no computador — e na simulação de celular do navegador — abriam a lista do
 * desktop, e cada celular abria o seletor do próprio sistema. A roleta é a
 * mesma em todo lugar.
 *
 * Arrastar, a roda do mouse ou as setas do teclado mudam; tocar num item o
 * traz ao centro. A escolha sobe quando a rolagem para. `confirmado` falso
 * mostra o item do centro em cinza: é só uma sugestão, que a pessoa ainda não
 * escolheu.
 */
export function Roleta({ rotulo, opcoes, valor, onChange, confirmado = true }: { rotulo: string; opcoes: Array<{ valor: string; texto: string }>; valor: string; onChange: (v: string) => void; confirmado?: boolean }) {
  const caixa = useRef<HTMLDivElement>(null);
  const parada = useRef(0);
  /*
   * Só a pessoa escolhe (A114). Para abrir no item certo a roleta rola a si
   * mesma, e essa rolagem dispara o mesmo evento da rolagem da pessoa — no
   * quadro seguinte. Sem esta trava, a coluna que abre fora do primeiro item
   * (o ano, as semanas) "escolhia" a sugestão sozinha: a data piscava de
   * cinza para escolhida, o aviso "Role para escolher" sumia, e quem não
   * mexia avançava com 01/01 do ano sugerido gravado.
   */
  const mexeu = useRef(false);
  const id = useId();
  const indice = Math.max(0, opcoes.findIndex((o) => o.valor === valor));
  const [centro, setCentro] = useState(indice);

  // Segue o valor de fora: ao abrir e quando outra coluna muda as opções
  // (o dia 31 que some ao trocar para fevereiro).
  useLayoutEffect(() => {
    const el = caixa.current;
    if (!el) return;
    if (Math.abs(el.scrollTop - indice * ALTURA_DO_ITEM) > 1) el.scrollTop = indice * ALTURA_DO_ITEM;
    setCentro(indice);
  }, [indice, opcoes.length]);

  const aoRolar = () => {
    const el = caixa.current;
    if (!el) return;
    const i = Math.min(opcoes.length - 1, Math.max(0, Math.round(el.scrollTop / ALTURA_DO_ITEM)));
    setCentro(i);
    if (!mexeu.current) return;
    window.clearTimeout(parada.current);
    parada.current = window.setTimeout(() => {
      if (opcoes[i].valor !== valor || !confirmado) onChange(opcoes[i].valor);
    }, 120);
  };

  const irPara = (i: number) => {
    mexeu.current = true;
    const alvo = Math.min(opcoes.length - 1, Math.max(0, i));
    // Já no centro, não há rolagem para confirmar: confirma direto.
    if (alvo === centro) {
      if (!confirmado || opcoes[alvo].valor !== valor) onChange(opcoes[alvo].valor);
      return;
    }
    caixa.current?.scrollTo({ top: alvo * ALTURA_DO_ITEM, behavior: 'smooth' });
  };

  const teclas = (e: KeyboardEvent) => {
    const passos: Record<string, number> = { ArrowUp: -1, ArrowDown: 1, PageUp: -5, PageDown: 5 };
    if (e.key in passos) irPara(centro + passos[e.key]);
    else if (e.key === 'Home') irPara(0);
    else if (e.key === 'End') irPara(opcoes.length - 1);
    else return;
    e.preventDefault();
  };

  const desbotar = 'linear-gradient(to bottom, transparent 0, #000 38%, #000 62%, transparent 100%)';
  return (
    <div style={{ position: 'relative', flex: 1, minWidth: 0 }}>
      <style>{'.eva-roleta::-webkit-scrollbar{display:none}'}</style>
      <div aria-hidden style={{ position: 'absolute', left: 0, right: 0, top: ESPACO, height: ALTURA_DO_ITEM, borderRadius: 10, background: '#F6F6F6', pointerEvents: 'none' }} />
      <div
        ref={caixa}
        className="eva-roleta"
        role="listbox"
        aria-label={rotulo}
        aria-activedescendant={`${id}-${centro}`}
        tabIndex={0}
        onScroll={aoRolar}
        onKeyDown={teclas}
        onPointerDown={() => (mexeu.current = true)}
        onWheel={() => (mexeu.current = true)}
        onTouchStart={() => (mexeu.current = true)}
        style={{ position: 'relative', height: ALTURA_DO_ITEM * ITENS_VISIVEIS, overflowY: 'auto', scrollSnapType: 'y mandatory', scrollbarWidth: 'none', overscrollBehavior: 'contain', maskImage: desbotar, WebkitMaskImage: desbotar, borderRadius: 10 }}
      >
        <div style={{ height: ESPACO }} />
        {opcoes.map((o, i) => {
          const noCentro = i === centro;
          return (
            <div
              key={o.valor}
              id={`${id}-${i}`}
              role="option"
              aria-selected={noCentro}
              onClick={() => irPara(i)}
              style={{
                height: ALTURA_DO_ITEM,
                scrollSnapAlign: 'center',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontFamily: FONTE_TITULO,
                fontSize: noCentro ? 20 : 16,
                fontWeight: noCentro ? 700 : 500,
                color: noCentro ? (confirmado ? COR.primaria : COR.textoFraco) : '#B3B3B3',
                cursor: 'pointer',
                userSelect: 'none',
                fontVariantNumeric: 'tabular-nums',
                transition: 'font-size 120ms ease, color 120ms ease',
              }}
            >
              {o.texto}
            </div>
          );
        })}
        <div style={{ height: ESPACO }} />
      </div>
    </div>
  );
}

/** Data sugerida quando não há valor: hoje, se o intervalo chega a este ano; senão, 18 anos antes do fim. */
function dataSugerida(anoMinimo: number, anoMaximo: number): string {
  const hoje = new Date();
  if (anoMaximo >= hoje.getFullYear()) return hoje.toISOString().slice(0, 10);
  return `${Math.max(anoMinimo, anoMaximo - 18)}-01-01`;
}

/** `AAAAMMDD` parcial → `DD/MM/AAAA` parcial, conforme a pessoa digita. */
function mascaraDeData(digitos: string): string {
  const d = digitos.slice(0, 8);
  if (d.length <= 2) return d;
  if (d.length <= 4) return `${d.slice(0, 2)}/${d.slice(2)}`;
  return `${d.slice(0, 2)}/${d.slice(2, 4)}/${d.slice(4)}`;
}

/**
 * Data em três roletas (dia, mês, ano), com um campo `DD/MM/AAAA` acima
 * (14/10/2026, pedido da design): rolar ou digitar, os dois sincronizados. O
 * campo abre o teclado numérico; a data só vale quando existe (nada de 31/02)
 * e o ano cabe no intervalo — até lá, nada sobe e o erro diz o porquê.
 * Antes a ordem era mês/dia/ano, do protótipo, e só dava para rolar.
 *
 * `valor` e `onChange` usam `AAAA-MM-DD`; vazio até a pessoa mexer — a
 * roleta abre numa data sugerida (`padrao`), em cinza, e o "Continuar" de
 * quem usa segue travado até a primeira escolha.
 */
export function SeletorDeData({ id, valor, onChange, anoMinimo, anoMaximo, rotulo, padrao }: { id: string; valor: string; onChange: (v: string) => void; anoMinimo: number; anoMaximo: number; rotulo: string; padrao?: string }) {
  const [partes, setPartes] = useState(() => {
    const [ano, mes, dia] = (valor || padrao || dataSugerida(anoMinimo, anoMaximo)).split('-');
    const anoNoIntervalo = String(Math.min(anoMaximo, Math.max(anoMinimo, Number(ano))));
    return { ano: anoNoIntervalo, mes, dia };
  });
  // O que está no campo, só dígitos. Vazio enquanto a data é só sugestão.
  const [digitos, setDigitos] = useState(() => (valor ? valor.split('-').reverse().join('') : ''));
  const confirmado = Boolean(valor);
  const diasNoMes = new Date(Number(partes.ano), Number(partes.mes), 0).getDate();

  const mudar = (p: Partial<typeof partes>) => {
    const novo = { ...partes, ...p };
    const ultimo = new Date(Number(novo.ano), Number(novo.mes), 0).getDate();
    if (Number(novo.dia) > ultimo) novo.dia = String(ultimo).padStart(2, '0');
    setPartes(novo);
    setDigitos(`${novo.dia}${novo.mes}${novo.ano}`);
    onChange(`${novo.ano}-${novo.mes}-${novo.dia}`);
  };

  const erro = (() => {
    if (digitos.length < 8) return null;
    const dia = Number(digitos.slice(0, 2));
    const mes = Number(digitos.slice(2, 4));
    const ano = Number(digitos.slice(4));
    if (mes < 1 || mes > 12 || dia < 1 || dia > new Date(ano, mes, 0).getDate()) return 'Essa data não existe.';
    if (ano < anoMinimo || ano > anoMaximo) return `O ano precisa estar entre ${anoMinimo} e ${anoMaximo}.`;
    return null;
  })();

  const digitar = (texto: string) => {
    const d = texto.replace(/\D/g, '').slice(0, 8);
    setDigitos(d);
    const dia = d.slice(0, 2);
    const mes = d.slice(2, 4);
    const ano = d.slice(4);
    const completa = d.length === 8;
    const valida = completa && Number(mes) >= 1 && Number(mes) <= 12 && Number(dia) >= 1 && Number(dia) <= new Date(Number(ano), Number(mes), 0).getDate() && Number(ano) >= anoMinimo && Number(ano) <= anoMaximo;
    if (valida) {
      setPartes({ ano, mes, dia });
      onChange(`${ano}-${mes}-${dia}`);
    } else if (valor) {
      // Data incompleta ou impossível não fica escolhida: o "Continuar" volta a travar.
      onChange('');
    }
  };

  const meses = MESES.map((texto, i) => ({ valor: String(i + 1).padStart(2, '0'), texto }));
  const dias = Array.from({ length: diasNoMes }, (_, i) => ({ valor: String(i + 1).padStart(2, '0'), texto: String(i + 1) }));
  const anos = Array.from({ length: anoMaximo - anoMinimo + 1 }, (_, i) => ({ valor: String(anoMinimo + i), texto: String(anoMinimo + i) }));

  return (
    <div id={id} role="group" aria-label={rotulo} style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '14px 12px', borderRadius: 14, background: COR.branco }}>
      <input
        aria-label={`${rotulo} (DD/MM/AAAA)`}
        aria-invalid={Boolean(erro)}
        inputMode="numeric"
        autoComplete="off"
        placeholder="DD/MM/AAAA"
        value={mascaraDeData(digitos)}
        onChange={(e) => digitar(e.target.value)}
        style={{
          alignSelf: 'center',
          width: 160,
          height: 40,
          padding: '0 12px',
          borderRadius: 10,
          border: `1px solid ${erro ? 'var(--mantine-color-red-6)' : COR.borda}`,
          background: COR.fundo,
          fontFamily: FONTE_TITULO,
          fontSize: 18,
          fontWeight: 600,
          letterSpacing: '0.04em',
          textAlign: 'center',
          fontVariantNumeric: 'tabular-nums',
          color: confirmado ? COR.texto : COR.textoFraco,
          outlineColor: COR.destaque,
        }}
      />
      {erro && <span role="alert" style={{ fontSize: 12, color: 'var(--mantine-color-red-7)', textAlign: 'center' }}>{erro}</span>}
      <div style={{ display: 'flex', gap: 6 }}>
        <Roleta rotulo="Dia" opcoes={dias} valor={partes.dia} onChange={(dia) => mudar({ dia })} confirmado={confirmado} />
        <Roleta rotulo="Mês" opcoes={meses} valor={partes.mes} onChange={(mes) => mudar({ mes })} confirmado={confirmado} />
        <Roleta rotulo="Ano" opcoes={anos} valor={partes.ano} onChange={(ano) => mudar({ ano })} confirmado={confirmado} />
      </div>
      {!confirmado && !erro && <span style={{ fontSize: 12, color: COR.textoFraco, textAlign: 'center' }}>Role ou digite a data</span>}
    </div>
  );
}
